// pages/TimesheetInsights.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  TablePagination,
  GlobalStyles,
  ListSubheader,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

/* ───────────── Helpers ───────────── */
const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const toISO = (d) => {
  const dd = new Date(d);
  return `${dd.getFullYear()}-${pad(dd.getMonth() + 1)}-${pad(dd.getDate())}`;
};
const monthKey = (isoDate) => (isoDate || "").slice(0, 7); // YYYY-MM

const minutesToHM = (mins) => {
  const m = Math.max(0, Math.floor(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${pad(h)}:${pad(r)}`;
};
const minutesToDays = (mins) => (mins || 0) / 480; // 8 hours/day

// Stable color for each series
const PALETTE = [
  "#1E88E5", "#43A047", "#FB8C00", "#8E24AA", "#F4511E", "#00ACC1",
  "#3949AB", "#6D4C41", "#7CB342", "#00897B", "#5C6BC0", "#FDD835",
];
const colorFor = (label) => {
  let h = 0;
  const s = String(label || "");
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
  return PALETTE[Math.abs(h) % PALETTE.length];
};

// Normalizers & keys (match logic used elsewhere)
const norm = (s) => (s ?? "").toString().trim().toLowerCase();
const idNorm = (s) => norm(s).replace(/[^a-z0-9]/g, "");
const projectKey = (entityName, projectName) =>
  `${idNorm(entityName)}||${idNorm(projectName)}`;

// Accessors for projects/tasks (handles naming variations)
const getProjectId     = (p) => p?.id ?? p?.docId ?? p?.projectId ?? "";
const getProjectName   = (p) => p?.name ?? p?.projectName ?? p?.title ?? "";
const getProjectEntity = (p) => p?.entity ?? p?.entityName ?? "";

const getTaskProjectId   = (t) => t?.projectId ?? t?.project_id ?? "";
const getTaskProjectName = (t) => t?.project ?? t?.projectName ?? "";
const getTaskEntity      = (t) => t?.entity ?? t?.entityName ?? "";

// Normalize a timesheet doc to flat daily entries
const flattenTimesheetDoc = (doc) => {
  const base = {
    userId: doc.userId || doc.uid || "",
    userName: doc.userName || doc.name || "",
    companyId: doc.companyId || "",
  };

  // Daily doc format
  if (doc.dateKey && (doc.minutes != null || doc.hours != null)) {
    const minutes =
      doc.minutes != null
        ? parseInt(doc.minutes, 10)
        : Math.round(parseFloat(doc.hours || 0) * 60);
    return [
      {
        ...base,
        projectName: doc.projectName || doc.project || "",
        entityName: doc.entityName || doc.entity || "",
        areaName: doc.areaName || doc.area || "",
        dateKey: doc.dateKey,
        minutes: Number.isFinite(minutes) ? minutes : 0,
      },
    ];
  }

  // Nested rows/entries (fallback)
  const container = Array.isArray(doc.rows)
    ? doc.rows
    : Array.isArray(doc.entries)
    ? doc.entries
    : [];
  const out = [];
  for (const r of container) {
    const projectName = r.projectName || r.project || "";
    const entityName = r.entityName || r.entity || "";
    const areaName = r.areaName || r.area || "";
    const byDate = r.minutesByDate || r.daily || r.byDate || {};
    Object.keys(byDate).forEach((iso) => {
      out.push({
        ...base,
        projectName,
        entityName,
        areaName,
        dateKey: iso,
        minutes: parseInt(byDate[iso], 10) || 0,
      });
    });
  }
  return out;
};

// Build monthly series
const buildMonthly = (entries, groupKey) => {
  const byMonth = new Map(); // month -> Map(series -> minutes)
  entries.forEach((e) => {
    const m = monthKey(e.dateKey);
    const key = e[groupKey] || "(unknown)";
    if (!byMonth.has(m)) byMonth.set(m, new Map());
    const inner = byMonth.get(m);
    inner.set(key, (inner.get(key) || 0) + (e.minutes || 0));
  });

  const allSeries = new Set();
  byMonth.forEach((inner) => inner.forEach((_, k) => allSeries.add(k)));
  const columns = [...allSeries];

  const data = [...byMonth.keys()].sort().map((m) => {
    const row = { month: m };
    columns.forEach((k) => { row[k] = byMonth.get(m).get(k) || 0; });
    return row;
  });

  return { data, columns };
};

// Build one matrix: rows = (entity, project), columns = users
function buildProjectUserMatrix(entries) {
  const rowMap = new Map(); // "entity||project" -> Map(userName -> minutes)
  const userSet = new Set();

  entries.forEach((e) => {
    const rKey = `${e.entityName || "(unknown)"}||${e.projectName || "(unknown)"}`;
    const u = e.userName || "(unknown)";
    userSet.add(u);
    if (!rowMap.has(rKey)) rowMap.set(rKey, new Map());
    const inner = rowMap.get(rKey);
    inner.set(u, (inner.get(u) || 0) + (e.minutes || 0));
  });

  const columns = [...userSet].sort((a, b) => a.localeCompare(b));
  const rows = [...rowMap.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map((k) => {
      const [entityName, projectName] = k.split("||");
      const inner = rowMap.get(k);
      const obj = { __entity: entityName, __project: projectName };
      let sum = 0;
      columns.forEach((u) => {
        const v = inner.get(u) || 0;
        obj[u] = v;
        sum += v;
      });
      obj.__total = sum;
      return obj;
    });

  const grandTotal = rows.reduce((s, r) => s + r.__total, 0);
  return { rows, columns, grandTotal };
}

export default function TimesheetInsights() {
  const { companyId, user } = useAuth();

  // Role context
  const meUid = user?.uid || "";
  const meNameN = idNorm(user?.name || user?.displayName || "");
  const meRole = user?.role || "Member";

  // Filters
  const [from, setFrom] = useState(() =>
    toISO(new Date(new Date().getFullYear(), 0, 1))
  );
  const [to, setTo] = useState(() => toISO(new Date()));
  const [selUsers, setSelUsers] = useState([]); // [{uid,name}]
  const [selProjects, setSelProjects] = useState([]); // [projectName]
  const [selEntities, setSelEntities] = useState([]); // [entityName]
  const [selAreas, setSelAreas] = useState([]); // [areaName]
  const [mode, setMode] = useState("project"); // "project" | "user"
  const [unit, setUnit] = useState("hhmm"); // "hhmm" | "days"

  // UI multiselect label arrays + search inside menus
  const [userSel, setUserSel] = useState([]);
  const [projectSel, setProjectSel] = useState([]);
  const [entitySel, setEntitySel] = useState([]);
  const [areaSel, setAreaSel] = useState([]);

  const [userQuery, setUserQuery] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [entityQuery, setEntityQuery] = useState("");
  const [areaQuery, setAreaQuery] = useState("");

  // Pagination for matrix
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Data state
  const [loading, setLoading] = useState(false);
  const [rowsAll, setRowsAll] = useState([]); // flat daily entries (date-scoped)
  const [error, setError] = useState("");

  // Auxiliary: projects & tasks (for role scoping)
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);

  /* ---- Load Projects (always) ---- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!companyId) return;
      try {
        const pSnap = await getDocs(
          query(collection(db, "projects"), where("companyId", "==", companyId))
        );
        const p = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (mounted) setProjects(p);
      } catch (e) {
        console.error("Load projects failed:", e);
        if (mounted) setProjects([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [companyId]);

  /* ---- Load Tasks (needed for Member to discover their assigned projects) ---- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!companyId) return;
      // We fetch tasks for both Manager & Member and scope later; Admin doesn't strictly need them
      if (meRole === "Admin") {
        if (mounted) setTasks([]);
        return;
      }
      try {
        const tSnap = await getDocs(
          query(collection(db, "tasks"), where("companyId", "==", companyId))
        );
        const t = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (mounted) setTasks(t);
      } catch (e) {
        console.error("Load tasks failed:", e);
        if (mounted) setTasks([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [companyId, meRole]);

  /* ---- Load Timesheets (date-scoped) ---- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const qRef = query(
          collection(db, "timesheets"),
          where("companyId", "==", companyId || "")
        );
        const snap = await getDocs(qRef);
        const all = [];
        snap.forEach((d) => all.push({ id: d.id, ...d.data() }));

        const fISO = new Date(from);
        const tISO = new Date(to);
        const flat = all
          .flatMap(flattenTimesheetDoc)
          .filter((e) => {
            const d = new Date(e.dateKey);
            return !isNaN(d) && d >= fISO && d <= tISO;
          });

        if (mounted) setRowsAll(flat);
      } catch (e) {
        console.error(e);
        if (mounted) setError("Failed to load timesheets.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [companyId, from, to]);

  /* ---- Build project lookup maps ---- */
  const projectById = useMemo(() => {
    const m = new Map();
    for (const p of projects) {
      const pid = getProjectId(p);
      if (pid) m.set(pid, p);
    }
    return m;
  }, [projects]);

  const managerProjectKeys = useMemo(() => {
    if (meRole !== "Manager") return new Set();
    const s = new Set();
    for (const p of projects) {
      const isMine =
        p?.managerId === meUid ||
        (p?.managerName && idNorm(p.managerName) === meNameN);
      if (isMine) {
        s.add(projectKey(getProjectEntity(p), getProjectName(p)));
      }
    }
    return s;
  }, [projects, meRole, meUid, meNameN]);

  const tlProjectKeys = useMemo(() => {
    const s = new Set();
    for (const p of projects) {
      const tlId = p?.teamLeadId || p?.teamLeaderId;
      const tlName = p?.teamLeadName || p?.teamLeaderName || "";
      if ((tlId && tlId === meUid) || (tlName && idNorm(tlName) === meNameN)) {
        s.add(projectKey(getProjectEntity(p), getProjectName(p)));
      }
    }
    return s;
  }, [projects, meUid, meNameN]);

  const myAssignedProjectKeys = useMemo(() => {
    if (meRole === "Admin") return new Set(); // not used for Admin
    const s = new Set();
    for (const t of tasks) {
      const assignedTo = idNorm(t?.assignedTo || "");
      if (assignedTo !== meNameN) continue;

      // Prefer projectId → map to project for reliable entity/name
      const pid = getTaskProjectId(t);
      if (pid && projectById.has(pid)) {
        const p = projectById.get(pid);
        s.add(projectKey(getProjectEntity(p), getProjectName(p)));
        continue;
      }

      // Fallback to task's own names
      s.add(projectKey(getTaskEntity(t), getTaskProjectName(t)));
    }
    return s;
  }, [tasks, projectById, meNameN, meRole]);

  /* ---- Apply role visibility to time entries ---- */
  const rowsScoped = useMemo(() => {
    if (meRole === "Admin") {
      return rowsAll; // no restriction
    }

    // Manager: include entries for projects they manage (all users)
    if (meRole === "Manager") {
      return rowsAll.filter((e) =>
        managerProjectKeys.has(projectKey(e.entityName, e.projectName))
      );
    }

    // Member:
    // - If TL for project → include all users' entries for those projects
    // - Else if assigned on a project → include only own entries for those projects
    const out = [];
    for (const e of rowsAll) {
      const pKey = projectKey(e.entityName, e.projectName);
      if (tlProjectKeys.has(pKey)) {
        out.push(e); // all users on TL projects
      } else if (myAssignedProjectKeys.has(pKey)) {
        const isMe =
          (e.userId && e.userId === meUid) ||
          (e.userName && idNorm(e.userName) === meNameN);
        if (isMe) out.push(e);
      }
      // else: not visible
    }
    return out;
  }, [
    rowsAll,
    meRole,
    managerProjectKeys,
    tlProjectKeys,
    myAssignedProjectKeys,
    meUid,
    meNameN,
  ]);

  /* ---- Filter options built from role-scoped rows ---- */
  const usersForFilter = useMemo(() => {
    const set = new Set(rowsScoped.map((r) => `${r.userId}||${r.userName}`));
    return [...set]
      .map((k) => {
        const [uid, name] = k.split("||");
        return { uid, name };
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [rowsScoped]);

  const projectsForFilter = useMemo(() => {
    const set = new Set(rowsScoped.map((r) => r.projectName || ""));
    return [...set].filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [rowsScoped]);

  const entitiesForFilter = useMemo(() => {
    const set = new Set(rowsScoped.map((r) => r.entityName || ""));
    return [...set].filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [rowsScoped]);

  const areasForFilter = useMemo(() => {
    const set = new Set(rowsScoped.map((r) => r.areaName || ""));
    return [...set].filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [rowsScoped]);

  // Flat option arrays for menus
  const userOptions = useMemo(
    () => usersForFilter.map((u) => u.name).sort(),
    [usersForFilter]
  );
  const projectOptions = useMemo(() => [...projectsForFilter], [projectsForFilter]);
  const entityOptions = useMemo(() => [...entitiesForFilter], [entitiesForFilter]);
  const areaOptions = useMemo(() => [...areasForFilter], [areasForFilter]);

  // Convert selected labels to filter state used below
  useEffect(() => {
    if (!userSel.length) {
      setSelUsers([]);
      return;
    }
    const byName = new Map(usersForFilter.map((u) => [u.name, u.uid]));
    const mapped = userSel
      .map((name) => ({ uid: byName.get(name), name }))
      .filter((x) => x.uid); // drop unknowns
    setSelUsers(mapped);
  }, [userSel, usersForFilter]);

  useEffect(() => {
    setSelProjects(projectSel);
  }, [projectSel]);
  useEffect(() => {
    setSelEntities(entitySel);
  }, [entitySel]);
  useEffect(() => {
    setSelAreas(areaSel);
  }, [areaSel]);

  /* ---- Apply UI filters on top of role-scoped rows ---- */
  const filtered = useMemo(() => {
    const userSet = new Set(selUsers.map((u) => u.uid));
    const projSet = new Set(selProjects);
    const entSet = new Set(selEntities);
    const areaSet = new Set(selAreas);

    return rowsScoped.filter((r) => {
      const okUser = userSet.size ? userSet.has(r.userId) : true;
      const okProj = projSet.size ? projSet.has(r.projectName || "") : true;
      const okEnt = entSet.size ? entSet.has(r.entityName || "") : true;
      const okArea = areaSet.size ? areaSet.has(r.areaName || "") : true;
      return okUser && okProj && okEnt && okArea;
    });
  }, [rowsScoped, selUsers, selProjects, selEntities, selAreas]);

  /* ---- Build chart data ---- */
  const projMonthly = useMemo(() => buildMonthly(filtered, "projectName"), [filtered]);
  const userMonthly = useMemo(() => buildMonthly(filtered, "userName"), [filtered]);
  const current = mode === "project" ? projMonthly : userMonthly;

  const seriesKeys = useMemo(() => current?.columns || [], [current]);
  const chartData = useMemo(() => {
    return (current?.data || []).map((row) => {
      const out = { month: row.month };
      (seriesKeys || []).forEach((k) => {
        const mins = row[k] || 0;
        out[k] = unit === "days" ? Math.round(minutesToDays(mins) * 100) / 100 : mins;
      });
      return out;
    });
  }, [current, seriesKeys, unit]);

  /* ---- Totals table ---- */
  const totalsTable = useMemo(() => {
    const byMonth = new Map();
    filtered.forEach((e) => {
      const m = monthKey(e.dateKey);
      byMonth.set(m, (byMonth.get(m) || 0) + (e.minutes || 0));
    });
    return [...byMonth.keys()].sort().map((m) => ({
      month: m,
      totalMinutes: byMonth.get(m),
    }));
  }, [filtered]);

  /* ---- Project(entity) × User matrix ---- */
  const matrix = useMemo(() => buildProjectUserMatrix(filtered), [filtered]);

  // Formatters for axis/tooltip/table based on unit
  const formatYAxis = (v) => (unit === "days" ? v.toFixed(2) : minutesToHM(v));
  const tipFormatter = (value) =>
    unit === "days" ? `${Number(value).toFixed(2)} d` : minutesToHM(value);
  const tableCell = (mins) => (unit === "days" ? minutesToDays(mins).toFixed(2) : minutesToHM(mins));

  // Pagination handlers
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Slice rows for pagination
  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return matrix.rows.slice(start, start + rowsPerPage);
  }, [matrix.rows, page, rowsPerPage]);

  // Clear all UI filters quickly
  const clearFilters = () => {
    setSelUsers([]);
    setSelProjects([]);
    setSelEntities([]);
    setSelAreas([]);
    setUserSel([]);
    setProjectSel([]);
    setEntitySel([]);
    setAreaSel([]);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Let Recharts colors win over any global fill rules */}
      <GlobalStyles
        styles={{
          ".recharts-wrapper .recharts-rectangle path, .recharts-wrapper .recharts-legend-icon ": {
            fill: "unset !important",
            stroke: "unset !important",
          },
        }}
      />

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Timesheet Insights
      </Typography>

      {/* Filters */}
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          bgcolor: "#F8FAFF",
          border: "1px solid #E8EEF9",
        }}
      >
        <Grid container spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
          {/* Dates */}
          <Grid item xs={12} sm="auto">
            <TextField
              size="small"
              label="From"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 60 }}
            />
          </Grid>

          <Grid item xs={12} sm="auto">
            <TextField
              size="small"
              label="To"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 60 }}
            />
          </Grid>

          {/* Users (multi-select with inline search) */}
          <Grid item xs={12} sm="auto">
          <TextField
            select
            size="small"
            label="Users"
            value={userSel}
            onChange={(e) =>
              setUserSel(
                Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",")
              )
            }
            SelectProps={{
              multiple: true,
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 150 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                size="small"
                placeholder="Search users…"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                fullWidth
              />
            </ListSubheader>

            <MenuItem
              value="__all__"
              onClick={() => setUserSel(userSel.length === userOptions.length ? [] : userOptions)}
            >
              <Checkbox
                size="small"
                checked={userSel.length === userOptions.length && userOptions.length > 0}
                indeterminate={userSel.length > 0 && userSel.length < userOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>

            {userOptions
              .filter((v) => v.toLowerCase().includes(userQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={userSel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>
          </Grid>

          {/* Projects */}
          <Grid item xs={12} sm="auto">
          <TextField
            select
            size="small"
            label="Projects"
            value={projectSel}
            onChange={(e) =>
              setProjectSel(
                Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",")
              )
            }
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 200 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                size="small"
                placeholder="Search projects…"
                value={projectQuery}
                onChange={(e) => setProjectQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                fullWidth
              />
            </ListSubheader>

            <MenuItem
              value="__all__"
              onClick={() =>
                setProjectSel(projectSel.length === projectOptions.length ? [] : projectOptions)
              }
            >
              <Checkbox
                size="small"
                checked={projectSel.length === projectOptions.length && projectOptions.length > 0}
                indeterminate={projectSel.length > 0 && projectSel.length < projectOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>

            {projectOptions
              .filter((v) => v.toLowerCase().includes(projectQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={projectSel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>
          </Grid>

          {/* Entities */}
          <Grid item xs={12} sm="auto">
          <TextField
            select
            size="small"
            label="Entities"
            value={entitySel}
            onChange={(e) =>
              setEntitySel(
                Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",")
              )
            }
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 180 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                size="small"
                placeholder="Search entities…"
                value={entityQuery}
                onChange={(e) => setEntityQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                fullWidth
              />
            </ListSubheader>

            <MenuItem
              value="__all__"
              onClick={() =>
                setEntitySel(entitySel.length === entityOptions.length ? [] : entityOptions)
              }
            >
              <Checkbox
                size="small"
                checked={entitySel.length === entityOptions.length && entityOptions.length > 0}
                indeterminate={entitySel.length > 0 && entitySel.length < entityOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>

            {entityOptions
              .filter((v) => v.toLowerCase().includes(entityQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={entitySel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>
          </Grid>

          {/* Areas / Processes */}
          <Grid item xs={12} sm="auto">
          <TextField
            select
            size="small"
            label="Areas / Processes"
            value={areaSel}
            onChange={(e) =>
              setAreaSel(
                Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",")
              )
            }
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected.length ? `${selected.length} selected` : "All"),
              MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            }}
            sx={{ minWidth: 180 }}
          >
            <ListSubheader sx={{ position: "sticky", top: 0, bgcolor: "background.paper", p: 1 }}>
              <TextField
                size="small"
                placeholder="Search areas/processes…"
                value={areaQuery}
                onChange={(e) => setAreaQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                fullWidth
              />
            </ListSubheader>

            <MenuItem
              value="__all__"
              onClick={() => setAreaSel(areaSel.length === areaOptions.length ? [] : areaOptions)}
            >
              <Checkbox
                size="small"
                checked={areaSel.length === areaOptions.length && areaOptions.length > 0}
                indeterminate={areaSel.length > 0 && areaSel.length < areaOptions.length}
              />
              <ListItemText primary="Select all" />
            </MenuItem>

            {areaOptions
              .filter((v) => v.toLowerCase().includes(areaQuery.toLowerCase()))
              .map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox size="small" checked={areaSel.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
          </TextField>
          </Grid>

          {/* Right-side toggles + Clear */}
          <Grid item xs sx={{ minWidth: 0 }} />

          <Grid item xs="auto">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ mr: 1 }}
            >
              <ToggleButton value="project">Project</ToggleButton>
              <ToggleButton value="user">User</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={unit}
              onChange={(_, v) => v && setUnit(v)}
              sx={{ mr: 1 }}
            >
              <ToggleButton value="hhmm">hh:mm</ToggleButton>
              <ToggleButton value="days">Days</ToggleButton>
            </ToggleButtonGroup>

            <Chip label="Clear" onClick={clearFilters} variant="outlined" size="small" sx={{ cursor: "pointer" }} />
          </Grid>
        </Grid>
      </Paper>


{/* Matrix with pagination */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          Project (entity) × User — Totals for selected period ({unit === "days" ? "days" : "hh:mm"})
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader sx={{ width: "max-content", minWidth: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ minWidth: 180, fontWeight: 700, position: "sticky", left: 0, background: "white", zIndex: 1 }}
                >
                  Entity
                </TableCell>
                <TableCell
                  sx={{ minWidth: 220, fontWeight: 700, position: "sticky", left: 180, background: "white", zIndex: 1 }}
                >
                  Project
                </TableCell>
                {matrix.columns.map((u) => (
                  <TableCell key={u} align="right" sx={{ whiteSpace: "nowrap" }}>
                    {u}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((r, idx) => (
                <TableRow key={`${r.__entity}||${r.__project}||${idx}`}>
                  <TableCell sx={{ position: "sticky", left: 0, background: "white", minWidth: 180 }}>
                    {r.__entity}
                  </TableCell>
                  <TableCell sx={{ position: "sticky", left: 180, background: "white", minWidth: 220 }}>
                    {r.__project}
                  </TableCell>
                  {matrix.columns.map((u) => (
                    <TableCell key={u} align="right">
                      {tableCell(r[u] || 0)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {tableCell(r.__total)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700, position: "sticky", left: 0, background: "white" }}>
                  Grand Total
                </TableCell>
                <TableCell sx={{ position: "sticky", left: 180, background: "white" }} />
                {matrix.columns.map((u) => (
                  <TableCell key={u} align="right" sx={{ fontWeight: 700 }}>
                    {tableCell(matrix.rows.reduce((s, r) => s + (r[u] || 0), 0))}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {tableCell(matrix.grandTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={matrix.rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
        />
      </Paper>


      {/* Chart */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography sx={{ mb: 1, fontWeight: 600 }}>
          Monthly {mode === "project" ? "Project" : "User"} Totals ({unit === "days" ? "days" : "hh:mm"})
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 18, left: 10, bottom: 16 }} barCategoryGap="32%" barSize={22}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip formatter={tipFormatter} cursor={false} />
              <Legend
                payload={(seriesKeys || []).map((key) => ({
                  id: key,
                  value: key,
                  type: "square",
                  color: colorFor(key),
                }))}
              />
              {(seriesKeys || []).map((key) => {
                const c = colorFor(key);
                return <Bar key={key} dataKey={key} fill={c} stroke={c} radius={[4, 4, 0, 0]} />;
              })}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Monthly totals under chart */}
        <Typography sx={{ mb: 1, fontWeight: 600 }}>Monthly Totals</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month (YYYY-MM)</TableCell>
              <TableCell align="right">{unit === "days" ? "Total (days)" : "Total (hh:mm)"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {totalsTable.map((r) => (
              <TableRow key={r.month}>
                <TableCell>{r.month}</TableCell>
                <TableCell align="right">
                  {unit === "days" ? minutesToDays(r.totalMinutes).toFixed(2) : minutesToHM(r.totalMinutes)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Grand Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {unit === "days"
                  ? minutesToDays(totalsTable.reduce((s, r) => s + r.totalMinutes, 0)).toFixed(2)
                  : minutesToHM(totalsTable.reduce((s, r) => s + r.totalMinutes, 0))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      
    </Box>
  );
}
